'use client';

import { useMemo } from 'react';
import type { Variants } from 'motion/react';
import { useInstantMotion } from '@/hooks/use-instant-motion';
import { useIsMobile } from '@/hooks/use-mobile';

export const ease = [0.22, 1, 0.36, 1] as const;
export const heroFollowRevealDelay = 1.55;

export const sectionHeaderRevealVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

export const sectionHeaderItemVariants: Variants = {
  hidden: { opacity: 0, y: -24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease },
  },
};

export const sectionGridRevealVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.42,
      staggerChildren: 0.09,
    },
  },
};

export const sectionCardRevealVariants: Variants = {
  hidden: { opacity: 0, y: 28, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.65, ease },
  },
};

export const servicesGridRevealVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.9,
      staggerChildren: 0.09,
    },
  },
};

export const servicesTileRevealVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.65, ease },
  },
};

export const servicesDialogContentVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.08,
    },
  },
};

export const servicesDialogItemVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease },
  },
};

export const servicesDetailGridVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

export function useServiceMotionVariants() {
  const instantMotion = useInstantMotion();
  const isMobile = useIsMobile();

  return useMemo(() => {
    if (instantMotion) {
      return {
        servicesGridRevealVariants: motionInstantParent,
        servicesTileRevealVariants: motionInstantItem,
      };
    }

    const gridRevealVariants: Variants = isMobile
      ? {
          hidden: {},
          visible: {
            transition: {
              delayChildren: 0.05,
              staggerChildren: 0.032,
            },
          },
        }
      : servicesGridRevealVariants;

    const tileRevealVariants: Variants = isMobile
      ? {
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { duration: 0.32, ease },
          },
        }
      : servicesTileRevealVariants;

    return {
      servicesGridRevealVariants: gridRevealVariants,
      servicesTileRevealVariants: tileRevealVariants,
    };
  }, [instantMotion, isMobile]);
}

/** Legal pages: hero title + date, then body blocks (Portable Text). */
export const legalDocumentHeaderRevealVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.06,
      staggerChildren: 0.12,
    },
  },
};

export const legalDocumentHeaderItemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.52, ease },
  },
};

export const legalDocumentBodyRevealVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.32,
      staggerChildren: 0.075,
    },
  },
};

export const legalDocumentBodyItemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.48, ease },
  },
};

export const legalDocumentMotionInstant: Variants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1, transition: { duration: 0 } },
};

const motionInstantItem: Variants = {
  hidden: { opacity: 1, y: 0, scale: 1 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0 } },
};

const motionInstantParent: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0, delayChildren: 0 } },
};

/** Stagger delays for carousel slides (by source index). Capped so looped tracks do not compound delay. */
export function getCarouselSlideRevealTransition(slideIndex: number, isMobile: boolean) {
  const stagger = isMobile ? 0.032 : 0.09;
  const maxDelay = isMobile ? 0.18 : 0.42;
  return {
    duration: isMobile ? 0.3 : 0.5,
    delay: Math.min(Math.max(0, slideIndex) * stagger, maxDelay),
    ease: [0.16, 1, 0.3, 1] as const,
  };
}

export function useSectionMotionVariants() {
  const instantMotion = useInstantMotion();
  const isMobile = useIsMobile();

  return useMemo(() => {
    if (instantMotion) {
      return {
        sectionHeaderRevealVariants: motionInstantParent,
        sectionHeaderItemVariants: motionInstantItem,
        sectionSequenceRevealVariants: motionInstantParent,
        sectionSequenceBlockVariants: motionInstantItem,
        sectionGridRevealVariants: motionInstantParent,
        sectionCardRevealVariants: motionInstantItem,
      };
    }

    const headerRevealVariants: Variants = {
      hidden: {},
      visible: {
        transition: {
          staggerChildren: isMobile ? 0.05 : 0.12,
        },
      },
    };

    const headerItemVariants: Variants = {
      hidden: { opacity: 0, y: isMobile ? -8 : -24 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: isMobile ? 0.42 : 0.7, ease },
      },
    };

    /** Section flow: header block → CTA → main content (top-to-bottom). */
    const sequenceRevealVariants: Variants = {
      hidden: {},
      visible: {
        transition: {
          delayChildren: isMobile ? 0.02 : 0.05,
          staggerChildren: isMobile ? 0.07 : 0.16,
        },
      },
    };

    /** Single block in a sequenced section (e.g. CTA row, carousel shell). */
    const sequenceBlockVariants: Variants = {
      hidden: {
        opacity: 0,
        y: isMobile ? 6 : 14,
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: isMobile ? 0.34 : 0.52, ease },
      },
    };

    const gridRevealVariants: Variants = {
      hidden: {},
      visible: {
        transition: {
          // Slightly shorter lead-in when nested after a sequence parent has already staggered.
          delayChildren: isMobile ? 0.06 : 0.22,
          staggerChildren: isMobile ? 0.04 : 0.085,
        },
      },
    };

    const cardRevealVariants: Variants = {
      hidden: {
        opacity: 0,
        y: isMobile ? 10 : 28,
        scale: isMobile ? 1 : 0.98,
      },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: isMobile ? 0.42 : 0.65, ease },
      },
    };

    return {
      sectionHeaderRevealVariants: headerRevealVariants,
      sectionHeaderItemVariants: headerItemVariants,
      sectionSequenceRevealVariants: sequenceRevealVariants,
      sectionSequenceBlockVariants: sequenceBlockVariants,
      sectionGridRevealVariants: gridRevealVariants,
      sectionCardRevealVariants: cardRevealVariants,
    };
  }, [instantMotion, isMobile]);
}
